import React, { useEffect, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import api from "../services/api";
import { useUser } from "../context/UserContext";
import { getStoredTheme, saveTheme } from "../utils/theme";
import {
  getStoredNavigationLayout,
  NAVIGATION_LAYOUT_OPTIONS,
  saveNavigationLayout,
} from "../navigation/navigationLayout";

const OPTIONS = [
  { value: "default", label: "Default" },
  { value: "high-contrast", label: "High contrast" },
  { value: "dark", label: "Dark mode" },
  { value: "large-text", label: "Large text" },
];

export default function DisplaySettingsPage() {
  const { logout, user } = useUser();
  const isMonteur = (user?.roles || []).some((role) => {
    const name = typeof role === "string" ? role : role?.name || role?.naam;
    return String(name || "").trim().toLowerCase() === "monteur";
  });
  const [selectedTheme, setSelectedTheme] = useState("default");
  const [navigationLayout, setNavigationLayout] = useState("topMegaMenu");
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState("");
  const [sessionActionId, setSessionActionId] = useState("");
  const [sessionMessage, setSessionMessage] = useState("");

  const [passkeys, setPasskeys] = useState([]);
  const [passkeysLoading, setPasskeysLoading] = useState(true);
  const [passkeyActionId, setPasskeyActionId] = useState("");
  const [passkeyName, setPasskeyName] = useState("");
  const [passkeyError, setPasskeyError] = useState("");
  const [passkeyMessage, setPasskeyMessage] = useState("");

  useEffect(() => {
    setSelectedTheme(getStoredTheme());
    setNavigationLayout(getStoredNavigationLayout());
  }, []);

  useEffect(() => {
    loadSessions();
    loadPasskeys();
  }, []);

  function handleSelect(theme) {
    const nextTheme = saveTheme(theme);
    setSelectedTheme(nextTheme);
  }

  function handleNavigationLayoutSelect(value) {
    const nextLayout = saveNavigationLayout(value);
    setNavigationLayout(nextLayout);
  }

  async function loadSessions() {
    setSessionsLoading(true);
    setSessionsError("");
    try {
      const res = await api.get("/auth/sessions");
      setSessions(Array.isArray(res?.data?.sessions) ? res.data.sessions : []);
    } catch (err) {
      setSessionsError(err?.response?.data?.message || "Sessies laden mislukt");
    } finally {
      setSessionsLoading(false);
    }
  }

  async function loadPasskeys() {
    setPasskeysLoading(true);
    setPasskeyError("");
    try {
      const res = await api.get("/auth/passkeys");
      setPasskeys(Array.isArray(res?.data?.passkeys) ? res.data.passkeys : []);
    } catch (err) {
      setPasskeyError(err?.response?.data?.message || "Passkeys laden mislukt");
    } finally {
      setPasskeysLoading(false);
    }
  }

  async function registerPasskey() {
    if (!window.PublicKeyCredential) {
      setPasskeyError("Passkeys worden niet ondersteund op dit apparaat of in deze browser.");
      return;
    }

    setPasskeyActionId("register");
    setPasskeyError("");
    setPasskeyMessage("");
    try {
      const displayName = String(passkeyName || "").trim();
      const startRes = await api.post("/auth/passkeys/register/start", {
        name: displayName || undefined,
      });

      const options = startRes?.data?.options;
      const challengeToken = String(startRes?.data?.challengeToken || "").trim();
      if (!options || !challengeToken) {
        throw new Error("Passkey challenge ontvangen mislukt");
      }

      const credential = await startRegistration({ optionsJSON: options });

      const finishRes = await api.post("/auth/passkeys/register/finish", {
        challengeToken,
        response: credential,
        name: displayName || undefined,
      });

      setPasskeyName("");
      setPasskeyMessage(finishRes?.data?.message || "Passkey geregistreerd");
      await loadPasskeys();
    } catch (err) {
      const isCancelled =
        err?.name === "AbortError" ||
        String(err?.message || "").toLowerCase().includes("cancel");
      if (!isCancelled) {
        setPasskeyError(
          err?.response?.data?.message ||
            err?.message ||
            "Passkey registreren mislukt"
        );
      }
    } finally {
      setPasskeyActionId("");
    }
  }

  async function removePasskey(credentialId) {
    const id = String(credentialId || "").trim();
    if (!id) return;

    setPasskeyActionId(id);
    setPasskeyError("");
    setPasskeyMessage("");
    try {
      const res = await api.delete(`/auth/passkeys/${encodeURIComponent(id)}`);
      setPasskeyMessage(res?.data?.message || "Passkey verwijderd");
      await loadPasskeys();
    } catch (err) {
      setPasskeyError(err?.response?.data?.message || "Passkey verwijderen mislukt");
    } finally {
      setPasskeyActionId("");
    }
  }

  async function revokeSession(sessionId) {
    setSessionActionId(sessionId);
    setSessionsError("");
    setSessionMessage("");
    try {
      const res = await api.delete(`/auth/sessions/${sessionId}`);
      if (res?.data?.currentSessionRevoked) {
        logout();
        window.location.href = "/login";
        return;
      }
      setSessionMessage(res?.data?.message || "Sessie afgesloten");
      await loadSessions();
    } catch (err) {
      setSessionsError(err?.response?.data?.message || "Sessie afsluiten mislukt");
    } finally {
      setSessionActionId("");
    }
  }

  async function revokeOtherSessions() {
    setSessionActionId("others");
    setSessionsError("");
    setSessionMessage("");
    try {
      const res = await api.post("/auth/sessions/revoke-others");
      setSessionMessage(res?.data?.message || "Andere sessies afgesloten");
      await loadSessions();
    } catch (err) {
      setSessionsError(err?.response?.data?.message || "Andere sessies afsluiten mislukt");
    } finally {
      setSessionActionId("");
    }
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <h1 className="text-lg font-semibold text-gray-900">Display mode</h1>
        <p className="mt-1 text-sm text-gray-600">
          Kies een weergave met betere leesbaarheid.
        </p>
      </section>

      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="grid gap-2 sm:grid-cols-2">
          {OPTIONS.map((option) => {
            const active = selectedTheme === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`rounded-lg border px-4 py-3 text-left text-sm transition ${
                  active
                    ? "border-blue-600 bg-blue-50 text-blue-900"
                    : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </section>

      {!isMonteur && (
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Hoofdnavigatie</h2>
            <p className="mt-1 text-sm text-gray-600">
              Kies welke navigatie-indeling gebruikt wordt in de cockpit en binnen Bedrijfsinrichting.
            </p>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {NAVIGATION_LAYOUT_OPTIONS.map((option) => {
              const active = navigationLayout === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleNavigationLayoutSelect(option.value)}
                  className={`rounded-lg border px-4 py-3 text-left text-sm transition ${
                    active
                      ? "border-blue-600 bg-blue-50 text-blue-900"
                      : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  <span className="block font-medium">{option.label}</span>
                  <span className="mt-1 block text-xs text-gray-600">
                    {option.description}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Passkeys</h2>
            <p className="mt-1 text-sm text-gray-600">
              Koppel dit apparaat als veilige passkey voor sneller inloggen zonder wachtwoord.
            </p>
          </div>
          <button
            type="button"
            onClick={registerPasskey}
            disabled={Boolean(passkeyActionId)}
            className="rounded-lg border border-blue-200 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {passkeyActionId === "register" ? "Bezig..." : "Passkey toevoegen"}
          </button>
        </div>

        <div className="mt-3 max-w-md">
          <input
            type="text"
            value={passkeyName}
            onChange={(event) => setPasskeyName(event.target.value)}
            placeholder="Naam (optioneel), bijv. iPhone van Michel"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            maxLength={120}
          />
        </div>

        {passkeyMessage && (
          <p className="mt-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
            {passkeyMessage}
          </p>
        )}

        {passkeyError && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {passkeyError}
          </p>
        )}

        <div className="mt-4 space-y-3">
          {passkeysLoading ? (
            <p className="text-sm text-gray-500">Passkeys laden...</p>
          ) : passkeys.length === 0 ? (
            <p className="text-sm text-gray-500">Nog geen passkeys gekoppeld.</p>
          ) : (
            passkeys.map((item) => {
              const credentialId = String(item?.credentialId || "");
              const name = String(item?.name || "").trim();
              const transports = Array.isArray(item?.transports)
                ? item.transports.filter(Boolean).join(", ")
                : "";
              return (
                <div key={credentialId} className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="space-y-1 text-sm text-gray-700">
                      <p className="font-medium text-gray-900">{name || "Passkey zonder naam"}</p>
                      <p>Aangemaakt: {formatDateTime(item?.createdAt)}</p>
                      <p>Laatst gebruikt: {formatDateTime(item?.lastUsedAt)}</p>
                      <p>Type: {String(item?.deviceType || "onbekend")}</p>
                      <p>Back-up sync: {item?.backedUp ? "ja" : "nee"}</p>
                      <p>Transports: {transports || "onbekend"}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePasskey(credentialId)}
                      disabled={Boolean(passkeyActionId)}
                      className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {passkeyActionId === credentialId ? "Bezig..." : "Verwijderen"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Actieve sessies</h2>
            <p className="mt-1 text-sm text-gray-600">
              Bekijk op welke apparaten je bent ingelogd en sluit oude sessies af.
            </p>
          </div>
          <button
            type="button"
            onClick={revokeOtherSessions}
            disabled={sessionsLoading || sessionActionId === "others"}
            className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sessionActionId === "others" ? "Bezig..." : "Andere sessies afsluiten"}
          </button>
        </div>

        {sessionMessage && (
          <p className="mt-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
            {sessionMessage}
          </p>
        )}

        {sessionsError && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {sessionsError}
          </p>
        )}

        <div className="mt-4 space-y-3">
          {sessionsLoading ? (
            <p className="text-sm text-gray-500">Sessies laden...</p>
          ) : sessions.length === 0 ? (
            <p className="text-sm text-gray-500">Nog geen sessies geregistreerd. Log opnieuw in om sessietracking te starten.</p>
          ) : (
            sessions.map((session) => (
              <div
                key={session._id}
                className={`rounded-lg border px-4 py-3 ${
                  session.current ? "border-blue-300 bg-blue-50" : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-1 text-sm text-gray-700">
                    <p className="font-medium text-gray-900">
                      {session.deviceLabel}
                      {session.current ? " · huidige sessie" : ""}
                    </p>
                    <p>Aangemaakt: {formatDateTime(session.createdAt)}</p>
                    <p>Laatst actief: {formatDateTime(session.lastSeenAt)}</p>
                    <p>IP: {session.lastSeenIp || session.ipAddress || "-"}</p>
                    <p className="break-all text-xs text-gray-500">{session.userAgent || "Geen user-agent beschikbaar"}</p>
                    {!session.active && (
                      <p className="text-amber-700">
                        Afgesloten op {formatDateTime(session.revokedAt)}{session.revokedReason ? ` (${session.revokedReason})` : ""}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => revokeSession(session._id)}
                    disabled={!session.active || Boolean(sessionActionId)}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sessionActionId === session._id ? "Bezig..." : session.current ? "Deze sessie afsluiten" : "Sessie afsluiten"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
